import {
  Align,
  DatePickerFormField,
  IDatePickerPayload,
  IFormRender,
  Label,
  Link,
  Row,
  SelectFormField,
} from '@busybusy/webapp-react-ui';
import { InfoIcon } from 'assets/icons';
import classNames from 'classnames';
import { HeaderDialog, IconButton } from 'components';
import DayOfMonthFormField from 'components/foundation/form-fields/day/DayOfMonthFormField/DayOfMonthFormField';
import DayOfMonthRangeFormField from 'components/foundation/form-fields/day/DayOfMonthRangeFormField/DayOfMonthRangeFormField';
import DayOfWeekFormField from 'components/foundation/form-fields/day/DayOfWeekFormField/DayOfWeekFormField';
import OnboardingFormCoordinator, {
  IOnboardingFormField,
} from 'containers/onboarding/OnboardingFormCoordinator/OnboardingFormCoordinator';
import { useOpenable } from 'hooks';
import { isNil } from 'lodash';
import { Dispatch, Fragment, FunctionComponent, SetStateAction } from 'react';
import { DayOfWeek } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { ClassName } from 'types/ClassName';
import { DayOfWeekValue } from 'types/enum/DayOfWeek';
import { PayPeriodTypeValue } from 'types/enum/PayPeriodType';
import { getDayOfWeekNumberFromDayOfWeekValue } from 'utils/dateUtils';
import { getPayPeriodStartAndDayData } from 'utils/payPeriodUtils';
import { v_require } from 'utils/validations';

export interface IOwnerOnboardingPayrollSettingsProps {
  onPayPeriodFormSubmit: (formData: IPayrollData) => void;
  formData: IPayrollData;
  setFormData?: Dispatch<SetStateAction<IPayrollData>>;
  className?: ClassName;
  submitButtonText: string;
  showEffectiveDate?: boolean;
  renderLeftContent?: boolean;
  isHistory?: boolean;
  disableCaptcha?: boolean;
}

export interface IPayrollData {
  payPeriodType: PayPeriodTypeValue;
  overtimeStart?: DayOfWeekValue;
  weeklyStartDay: DayOfWeekValue;
  biWeeklyPayPeriodDay: string;
  monthlyDay: string;
  semiMonthlyDays: string[];
  effectiveDate?: IDatePickerPayload | undefined;
  token?: string;
}

const OwnerOnboardingPayrollSettings: FunctionComponent<IOwnerOnboardingPayrollSettingsProps> = (props) => {
  const {
    onPayPeriodFormSubmit,
    formData,
    setFormData,
    className,
    submitButtonText,
    showEffectiveDate,
    renderLeftContent,
    isHistory,
    disableCaptcha = false,
  } = props;

  const [t] = useTranslation();

  const payPeriodLengthOpenable = useOpenable();
  const overtimeBeginsEachOpenable = useOpenable();
  const datePickerOpenable = useOpenable();

  function renderPayPeriodLengthFormField(form: any, onFocus: () => void, onBlur: () => void) {
    function onPayPeriodSelect(value: PayPeriodTypeValue) {
      if (value !== formData.payPeriodType) {
        switch (formData.payPeriodType) {
          case PayPeriodTypeValue.BIWEEKLY:
            form.updateError('biWeeklyPayPeriodDay', { hasError: false, message: '' });
            break;
          case PayPeriodTypeValue.WEEKLY:
            form.updateError('weeklyStartDay', { hasError: false, message: '' });
            break;
          case PayPeriodTypeValue.MONTHLY:
            form.updateError('monthlyDay', { hasError: false, message: '' });
            break;
          case PayPeriodTypeValue.SEMIMONTHLY:
            form.updateError('semiMonthlyDays', { hasError: false, message: '' });
            break;
        }

        setFormData?.({ ...formData, effectiveDate: undefined, payPeriodType: value });
      }
    }

    return (
      <Fragment key="payPeriodType">
        <Row align={Align.CENTER}>
          <Label>{t('Pay period length')}</Label>
          <IconButton className={'mobile-info-icon'} svg={InfoIcon} onClick={payPeriodLengthOpenable.open} />
        </Row>
        <SelectFormField
          name="payPeriodType"
          form={form}
          onBlur={onBlur}
          onFocus={onFocus}
          onChange={onPayPeriodSelect}
          options={Object.values(PayPeriodTypeValue).map((value) => ({ value, label: t(value) }))}
        />
      </Fragment>
    );
  }

  function getPayPeriodBeginFormTitle() {
    if (formData.payPeriodType === PayPeriodTypeValue.BIWEEKLY) {
      return t('Pay period begins every other');
    } else if (formData.payPeriodType === PayPeriodTypeValue.WEEKLY) {
      return t('Pay period begins each');
    } else {
      return '';
    }
  }

  function getPayPeriodBeginFormFields() {
    if (formData.payPeriodType === PayPeriodTypeValue.BIWEEKLY) {
      return {
        bodyText: t('Select the day that corresponds to the beginning of each two-week pay period.'),
        headerText: t('On which day of the week does each pay period begin?'),
      };
    } else {
      return {
        bodyText: t('Select the day that corresponds to the beginning of each weekly pay period.'),
        headerText: t('On which day of the week does each pay period begin?'),
      };
    }
  }

  function renderPayPeriodBeginFormField(form: IFormRender<any>, onFocus: () => void, onBlur: () => void) {
    function onChange(value: DayOfWeekValue) {
      setFormData?.({ ...formData, effectiveDate: undefined, biWeeklyPayPeriodDay: '', weeklyStartDay: value });
    }
    return (
      <Fragment key="weeklyStartDay">
        <Label>{getPayPeriodBeginFormTitle()}:</Label>
        <DayOfWeekFormField name="weeklyStartDay" form={form} onBlur={onBlur} onFocus={onFocus} onChange={onChange} />
      </Fragment>
    );
  }

  function renderPayPeriodMonthlyField(form: any, onFocus: () => void, onBlur: () => void) {
    function handleChange(value: string) {
      setFormData?.({ ...formData, effectiveDate: undefined, monthlyDay: value });
    }

    return (
      <Fragment key="monthlyDay">
        <Label>{t('Start date')}:</Label>
        <DayOfMonthFormField name="monthlyDay" form={form} onFocus={onFocus} onBlur={onBlur} onChange={handleChange} />
      </Fragment>
    );
  }

  function renderPayPeriodForSemiMonthly(form: any, onFocus: () => void, onBlur: () => void) {
    const selectProps = {
      onBlur,
      onFocus,
    };

    function handleChange(value: (string | null)[]) {
      setFormData?.({ ...formData, effectiveDate: undefined, semiMonthlyDays: value.filter((item) => !isNil(item)) });
    }

    return (
      <Fragment key="semiMonthlyDays">
        <Label>{t('Monthly start dates')}:</Label>
        <DayOfMonthRangeFormField
          form={form}
          name="semiMonthlyDays"
          firstProps={selectProps}
          secondProps={selectProps}
          onChange={handleChange}
        />
      </Fragment>
    );
  }

  function renderOvertimeBeginFormField(form: any, onFocus: () => void, onBlur: () => void) {
    return (
      <Fragment key="overtimeStart">
        <Row align={Align.CENTER}>
          <Label>{t('Workweek begins each') + ':'}</Label>
          <IconButton className={'mobile-info-icon'} svg={InfoIcon} onClick={overtimeBeginsEachOpenable.open} />
        </Row>
        <DayOfWeekFormField
          name="overtimeStart"
          form={form}
          onBlur={onBlur}
          onFocus={onFocus}
          validations={[{ validation: v_require }]}
        />
      </Fragment>
    );
  }

  function renderEffectiveDateFormField(form: IFormRender<any>) {
    const values = getPayPeriodStartAndDayData(
      form.state.data.weeklyStartDay,
      form.state.data.payPeriodType,
      form.state.data.biWeeklyPayPeriodDay,
      form.state.data.semiMonthlyDays,
      form.state.data.monthlyDay
    );

    const disableWeekDays: DayOfWeek = {
      dayOfWeek: [0, 1, 2, 3, 4, 5, 6].filter(
        (num) => num !== getDayOfWeekNumberFromDayOfWeekValue(form.state.data.weeklyStartDay)
      ),
    };

    const disableDayOfMonth: number[] = [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31,
    ].filter((num) => num !== values.organization_pay_period_day_one && num !== values.organization_pay_period_day_two);

    return (
      <Fragment key="effectiveDate">
        <Label>{t('Effective Date')}:</Label>
        <DatePickerFormField
          name="effectiveDate"
          form={form}
          onClose={datePickerOpenable.close}
          onOpen={datePickerOpenable.open}
          isOpen={datePickerOpenable.isOpen}
          disableWeekDays={
            form.state.data.payPeriodType === PayPeriodTypeValue.BIWEEKLY ||
            form.state.data.payPeriodType === PayPeriodTypeValue.WEEKLY
              ? disableWeekDays
              : undefined
          }
          disableDayOfMonth={
            form.state.data.payPeriodType === PayPeriodTypeValue.MONTHLY ||
            form.state.data.payPeriodType === PayPeriodTypeValue.SEMIMONTHLY
              ? disableDayOfMonth
              : undefined
          }
          validations={[{ validation: v_require }]}
        />
      </Fragment>
    );
  }

  function getOnboardingBody() {
    return (
      <>
        <>{t('Select the day of the week that corresponds to the start of your workweek.')}</>
        <ul>
          <li>{t('A workweek is defined as a fixed period of seven consecutive days.')}</li>
          <li>{t('The workweek is the basis for overtime calculations.')}</li>
          <li>{t('The workweek does not have to coincide with your pay period, nor the calendar week.')}</li>
          <li>{t('Your workweek may begin on any day of the week.')}</li>
        </ul>
        <>
          {t('For more information, please see the')}{' '}
          <Link href="https://www.dol.gov/whd/overtime_pay.htm" newTab={true}>
            {' '}
            {t('U.S. Department of Labor website')}.
          </Link>
        </>
      </>
    );
  }

  function getPayPeriodLengthBody() {
    return (
      <>
        <>{t('Select the pay period type that corresponds to your payroll frequency.')}</>
        <ul>
          <li>{t('Weekly, 52 pay periods per year')}</li>
          <li>{t('Biweekly (every two weeks), 26 pay periods per year')}</li>
          <li>{t('Semimonthly (twice each month), 24 pay periods per year')}</li>
          <li>{t('Monthly, 12 pay periods per year')}</li>
        </ul>
      </>
    );
  }

  function getFormFields(): IOnboardingFormField[] {
    const payPeriodLength = {
      panelContent: {
        bodyText: getPayPeriodLengthBody(),
        headerText: t('How often do you process payroll?'),
        isTitle: false,
      },
      renderField: renderPayPeriodLengthFormField,
    };

    const payPeriodBegin = {
      panelContent: {
        ...getPayPeriodBeginFormFields(),
        isTitle: false,
      },
      renderField: renderPayPeriodBeginFormField,
    };

    let overtimeOrEffectiveDateField;

    if (formData.overtimeStart && !isHistory) {
      overtimeOrEffectiveDateField = {
        panelContent: {
          bodyText: getOnboardingBody(),
          headerText: t('Which day of the week is the start of each workweek for overtime calculations?'),
          isTitle: false,
        },
        renderField: renderOvertimeBeginFormField,
      };
    } else if (showEffectiveDate) {
      overtimeOrEffectiveDateField = {
        panelContent: {
          bodyText: t(
            `We'll use these settings to calculate your payroll hours and costs in real time so you don't have to.
            Select an effective date to determine when these changes will take place.`
          ),
          headerText: t('Payroll Settings'),
          isTitle: false,
        },
        renderField: renderEffectiveDateFormField,
      };
    }

    if (formData.payPeriodType === PayPeriodTypeValue.SEMIMONTHLY) {
      const semiMonthlyFormField = {
        panelContent: {
          bodyText: t(
            'Select the two dates in each month that start the new pay period. Example, the 1st and the 16th; or the 5th and the 20th.'
          ),
          headerText: t('What are the two dates that start each pay period?'),
          isTitle: false,
        },
        renderField: renderPayPeriodForSemiMonthly,
      };

      return overtimeOrEffectiveDateField
        ? [payPeriodLength, semiMonthlyFormField, overtimeOrEffectiveDateField]
        : [payPeriodLength, semiMonthlyFormField];
    } else if (formData.payPeriodType === PayPeriodTypeValue.MONTHLY) {
      const monthlyFormField = {
        panelContent: {
          bodyText: t('Select the date that starts the new pay period every month. Example') + ':' + t('1st, or 5th.'),
          headerText: t('What is the start date for each monthly pay period?'),
          isTitle: false,
        },
        renderField: renderPayPeriodMonthlyField,
      };

      return overtimeOrEffectiveDateField
        ? [payPeriodLength, monthlyFormField, overtimeOrEffectiveDateField]
        : [payPeriodLength, monthlyFormField];
    } else {
      return overtimeOrEffectiveDateField
        ? [payPeriodLength, payPeriodBegin, overtimeOrEffectiveDateField]
        : [payPeriodLength, payPeriodBegin];
    }
  }

  const classes = classNames(
    {
      'owner-onboarding-payroll-settings': true,
    },
    className
  );

  return (
    <>
      <OnboardingFormCoordinator
        className={classes}
        defaultHeader={isHistory ? t('Edit Pay Period History') : t('Edit Pay Period')}
        defaultBody={
          isHistory
            ? t('Updating past pay period changes will cause hours to be recalculated for the affected dates.')
            : t('You can also edit or delete previous pay period changes from your pay period history.')
        }
        formFields={getFormFields()}
        formButtonLabel={submitButtonText}
        formData={formData}
        onSubmit={onPayPeriodFormSubmit}
        shouldRenderLeftContent={renderLeftContent}
        disableCaptcha={disableCaptcha}
      />
      <HeaderDialog
        isOpen={payPeriodLengthOpenable.isOpen}
        title={t('Pay Period Length')}
        onClose={payPeriodLengthOpenable.close}
        divider={false}
      >
        <div className={'p-5'}>
          <span>{t('Select the pay period type that corresponds to your payroll frequency.')}</span>
          <ul>
            <li className={'mb-1'}>{t('Weekly, 52 pay periods per year')}</li>
            <li className={'mb-1'}>{t('Biweekly (every two weeks), 26 pay periods per year')}</li>
            <li className={'mb-1'}>{t('Semimonthly (twice each month), 24 pay periods per year')}</li>
            <li className={'mb-1'}>{t('Monthly, 12 pay periods per year')}</li>
          </ul>
        </div>
      </HeaderDialog>
      <HeaderDialog
        isOpen={overtimeBeginsEachOpenable.isOpen}
        title={t('Overtime')}
        onClose={overtimeBeginsEachOpenable.close}
        divider={false}
      >
        <div className={'p-5'}>
          <span>
            {t(
              'Select the day of the week that corresponds to the start of your workweek. This will be used to calculate employee overtime.'
            )}
          </span>
          <ul>
            <li className={'mb-1'}>{t('A workweek is defined as a fixed period of seven consecutive days.')}</li>
            <li className={'mb-1'}>{t('The workweek is the basis for overtime calculations.')}</li>
            <li className={'mb-1'}>
              {t('The workweek does not have to coincide with your pay period, nor the calendar week.')}
            </li>
            <li className={'mb-1'}>{t('Your workweek may begin on any day of the week.')}</li>
          </ul>
          <span>{t('For more information, please see the ')}</span>
          <Link href={'https://www.dol.gov/whd/overtime_pay.htm'} newTab={true}>
            {t('U.S. Department of Labor website.')}
          </Link>
        </div>
      </HeaderDialog>
    </>
  );
};

export default OwnerOnboardingPayrollSettings;
